import React, { FC, createRef, useState } from 'react';
import { SearchInput } from '../../components/search-input/search-input';
import { Container } from '../../components/container/container';
import {
  HeaderActionsDialog,
  HeaderActionsIconButton,
  HomeSearchInputLabel,
  HomeSearchInputWrap,
  HomeSearchWrap,
  HomeWrap,
  XviaHomeSearch,
  HomeSearchButton,
  HomeSearchSuggestionsBox,
  HomeSearchSuggestionsBoxWrapper,
  HomeSearchSuggestionsBoxList,
  HomeSearchSuggestionsBoxListItemWrapper,
  HomeSearchSuggestionsBoxListItem,
} from './style';
import SearchIcon from '@mui/icons-material/Search';
import { DialogContent, DialogTitle } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from '@plataformaxvia/design-system';
import { useMediaQueryHook } from '../../hooks/use-media-query';
import OptimizedImageComponent from '../../components/optimized-image';
import { HomeBanner } from '../../apis/banners';
import { Suggestion } from '../../apis/search-catalog';

interface SearchDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onClickSearch: (search: string) => void;
  isMediumScreen: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  searchInputValue?: string;
  setSearchInputValue?: (value: string) => void;
  suggestions: Suggestion[];
  onClickSearchSuggestion: (slug: string) => void;
}

const SearchDialog: React.FC<SearchDialogProps> = ({
  onClickSearch,
  open,
  setOpen,
  isMediumScreen,
  inputRef,
  searchInputValue,
  setSearchInputValue,
  suggestions,
  onClickSearchSuggestion,
}) => {
  return (
    <HeaderActionsDialog
      open={open}
      onClose={() => setOpen(false)}
      TransitionProps={{
        onEntered: () => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        },
      }}
    >
      <DialogTitle>
        <HeaderActionsIconButton
          aria-label="close"
          onClick={() => setOpen(false)}
          disableRipple
          className="dialog"
        >
          <CloseIcon />
        </HeaderActionsIconButton>
      </DialogTitle>
      <DialogContent sx={{ marginTop: '20px' }}>
        <SearchInput
          ref={inputRef}
          className="xvia-home-search__input"
          placeholder={'Pesquisar...'}
          onClickSearch={onClickSearch}
          aria-label="Campo de busca de serviços"
          id="home-search-input-services"
          onChange={(e) => {
            const value = e.target.value;
            setSearchInputValue?.(value);
          }}
          value={searchInputValue}
        />
        {suggestions.length > 0 && (
          <SearchSuggestions
            suggestions={suggestions}
            onClick={onClickSearchSuggestion}
            highlight={searchInputValue}
          />
        )}
      </DialogContent>
    </HeaderActionsDialog>
  );
};

export interface HomeSearchProps {
  onClickSearch: any;
  images: HomeBanner[];
  currentImage: HomeBanner;
  searchInputValue?: string;
  setSearchInputValue?: (value: string) => void;
  suggestions: Suggestion[];
  onClickSearchSuggestion: (value: string) => void;
}

export const HomeSearch: FC<HomeSearchProps> = ({
  onClickSearch,
  images,
  currentImage,
  searchInputValue,
  setSearchInputValue,
  suggestions,
  onClickSearchSuggestion,
}) => {
  const { isMediumScreen } = useMediaQueryHook();
  const [open, setOpen] = useState(false);
  const inputRef = createRef<HTMLInputElement>();

  return (
    <XviaHomeSearch
      isMediumScreen={isMediumScreen}
      role="search"
      aria-labelledby="home-search-heading"
    >
      <Container className="xvia-home-search__container" style={{ zIndex: 2 }}>
        <HomeWrap isMediumScreen={isMediumScreen}>
          <h2 id="home-search-heading" className="visually-hidden">
            Busca de Serviços
          </h2>
          {!isMediumScreen && (
            <span className="label">
              Olá, você está no Portal de Pernambuco
            </span>
          )}
          <HomeSearchWrap
            onClick={() => setOpen(true)}
            isMediumScreen={isMediumScreen}
          >
            <HomeSearchInputWrap isMediumScreen={isMediumScreen}>
              {!isMediumScreen && <SearchIcon />}
              <HomeSearchInputLabel aria-label="Campo de busca de serviços">{'Pesquisar...'}</HomeSearchInputLabel>
            </HomeSearchInputWrap>
            {isMediumScreen && (
              <HomeSearchButton aria-label="Pesqusar">
                <SearchIcon />
              </HomeSearchButton>
            )}
            {!isMediumScreen && (
              <Button
                label={'Pesquisar'}
                size="large"
                aria-label="Pesquisar"
                id="home-search-button"
              />
            )}
          </HomeSearchWrap>
          <SearchDialog
            inputRef={inputRef}
            open={open}
            setOpen={setOpen}
            onClickSearch={onClickSearch}
            isMediumScreen={isMediumScreen}
            searchInputValue={searchInputValue}
            setSearchInputValue={setSearchInputValue}
            suggestions={suggestions}
            onClickSearchSuggestion={onClickSearchSuggestion}
          />
        </HomeWrap>
      </Container>

      {!isMediumScreen &&
        images.map((image, index) => (
          <OptimizedImageComponent
            key={index}
            src={image?.url}
            alt={image?.description || 'Banner do Portal de Serviços'}
            className={currentImage === image ? 'show' : ''}
            fill
          />
        ))}
    </XviaHomeSearch>
  );
};

interface SearchSuggestionsProps {
  suggestions: Suggestion[];
  onClick: (slug: string) => void;
  highlight?: string;
}

const SearchSuggestions: FC<SearchSuggestionsProps> = ({
  suggestions,
  onClick,
  highlight,
}) => (
  <HomeSearchSuggestionsBoxWrapper>
    <HomeSearchSuggestionsBox>
      <HomeSearchSuggestionsBoxList>
        {suggestions.map((suggestion) => (
          <HomeSearchSuggestionsBoxListItemWrapper key={suggestion.slug}>
            <span onClick={() => onClick(suggestion.slug)}>
              <HomeSearchSuggestionsBoxListItem>
                {highlightMatch(suggestion.title, highlight)}
              </HomeSearchSuggestionsBoxListItem>
            </span>
          </HomeSearchSuggestionsBoxListItemWrapper>
        ))}
      </HomeSearchSuggestionsBoxList>
    </HomeSearchSuggestionsBox>
  </HomeSearchSuggestionsBoxWrapper>
);

const highlightMatch = (text: string, query?: string) => {
  if (!query) return text;

  const normalizedText = normalize(text);
  const normalizedQuery = normalize(query);

  const startIndex = normalizedText.indexOf(normalizedQuery);

  if (startIndex === -1) return text;

  const endIndex = startIndex + normalizedQuery.length;

  return (
    <>
      {text.slice(0, startIndex)}
      <strong>{text.slice(startIndex, endIndex)}</strong>
      {text.slice(endIndex)}
    </>
  );
};

const normalize = (str: string) =>
  str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
